import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Send, Plug, Droplets, Key, Home, Search, Leaf } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ArticleCard from '@/components/ArticleCard';
import { useQuery } from '@tanstack/react-query';
import { fetchArticles } from '@/services/articles';
import { Skeleton } from '@/components/ui/skeleton';
import RequestFormDialog from './RequestFormDialog';
interface ServiceItem {
  icon: React.ReactNode;
  nameKey: string;
  gradient: string;
  hoverGradient: string;
  serviceId: string;
}
interface SearchLandingViewProps {
  onProfessionSelect: (professionId: string, displayName: string) => void;
}
const SearchLandingView: React.FC<SearchLandingViewProps> = ({
  onProfessionSelect
}) => {
  const {
    t
  } = useTranslation();
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);

  // Professions with active professionals
  const popularServices: ServiceItem[] = [{
    icon: <Plug size={28} />,
    nameKey: 'popularServices.electrician',
    gradient: 'from-brand-amber/10 to-brand-amber/5',
    hoverGradient: 'group-hover:from-brand-amber/20 group-hover:to-brand-amber/10',
    serviceId: 'electrician'
  }, {
    icon: <Droplets size={28} />,
    nameKey: 'popularServices.plumber',
    gradient: 'from-brand-teal/10 to-brand-teal/5',
    hoverGradient: 'group-hover:from-brand-teal/20 group-hover:to-brand-teal/10',
    serviceId: 'plumber'
  }, {
    icon: <Key size={28} />,
    nameKey: 'popularServices.locksmith',
    gradient: 'from-brand-navy/10 to-brand-navy/5',
    hoverGradient: 'group-hover:from-brand-navy/20 group-hover:to-brand-navy/10',
    serviceId: 'locksmith'
  }, {
    icon: <Home size={28} />,
    nameKey: 'popularServices.homeInspector',
    gradient: 'from-brand-amber/10 to-brand-amber/5',
    hoverGradient: 'group-hover:from-brand-amber/20 group-hover:to-brand-amber/10',
    serviceId: 'home-inspector'
  }, {
    icon: <Search size={28} />,
    nameKey: 'popularServices.leakDetection',
    gradient: 'from-brand-teal/10 to-brand-teal/5',
    hoverGradient: 'group-hover:from-brand-teal/20 group-hover:to-brand-teal/10',
    serviceId: 'leak-detection'
  }, {
    icon: <Leaf size={28} />,
    nameKey: 'popularServices.gardening',
    gradient: 'from-brand-navy/10 to-brand-navy/5',
    hoverGradient: 'group-hover:from-brand-navy/20 group-hover:to-brand-navy/10',
    serviceId: 'gardening'
  }];

  // Fetch articles for slider
  const {
    data: articles = [],
    isLoading: articlesLoading
  } = useQuery({
    queryKey: ['search-landing-articles'],
    queryFn: async () => {
      const allArticles = await fetchArticles();
      return allArticles.slice(0, 3);
    },
    staleTime: 5 * 60 * 1000
  });
  const handleProfessionClick = (serviceId: string, displayName: string) => {
    onProfessionSelect(serviceId, displayName);
  };
  return <div className="space-y-12">
      {/* Submit Request CTA */}
      <div className="bg-gradient-to-br from-brand-teal/10 to-brand-navy/5 rounded-2xl p-8 text-center border border-border/50">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
          {t('search.submitRequestTitle')}
        </h2>
        <p className="text-muted-foreground mb-6 max-w-xl mx-auto leading-relaxed font-semibold text-blue-800">
          {t('search.submitRequestSubtitle')}
        </p>
        <Button size="lg" className="bg-[#00D09E] hover:bg-[#00C090] text-white px-8 py-6 text-lg" onClick={() => setRequestDialogOpen(true)}>
          <Send size={20} className="ml-2" />
          {t('search.submitRequest')}
        </Button>
        <p className="text-sm text-muted-foreground mt-3">
          {t('search.submitRequestFree')}
        </p>
      </div>

      {/* Popular Professions Grid */}
      <div>
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {t('search.orBrowseProfessions')}
          </h2>
          <p className="text-muted-foreground">
            {t('search.selectProfessionToSearch')}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {popularServices.map((service, idx) => {
          const displayName = t(service.nameKey);
          return <button key={idx} onClick={() => handleProfessionClick(service.serviceId, displayName)} className="group text-right">
                <div className={`flex flex-col items-center justify-center p-5 md:p-6 rounded-2xl bg-gradient-to-br ${service.gradient} ${service.hoverGradient} border border-border/50 hover:border-brand-teal/30 hover:shadow-lg hover:shadow-brand-teal/10 transition-all duration-300 group-hover:-translate-y-1`}>
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm group-hover:shadow-md group-hover:scale-110 transition-all duration-300">
                    <span className="text-brand-navy group-hover:text-brand-teal transition-colors duration-300">
                      {service.icon}
                    </span>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground text-center leading-tight">{displayName}</h3>
                </div>
              </button>;
        })}
        </div>
      </div>

      {/* Articles Section */}
      {(articlesLoading || articles.length > 0) && <div>
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {t('articles.title')} <span className="text-brand-teal">{t('articles.titleHighlight')}</span>
            </h2>
            <p className="text-muted-foreground">{t('articles.subtitle')}</p>
          </div>

          {articlesLoading ? <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl overflow-hidden shadow-md">
                  <Skeleton className="h-48 w-full" />
                  <div className="p-6">
                    <Skeleton className="h-4 w-20 mb-2" />
                    <Skeleton className="h-6 w-3/4 mb-3" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>)}
            </div> : <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles.map(article => <ArticleCard key={article.id} id={article.id} title={article.title} summary={article.summary || ''} image={article.image || '/placeholder.svg'} date={article.created_at ? new Date(article.created_at).toLocaleDateString('he-IL') : ''} category={article.category || undefined} author={article.author || undefined} />)}
            </div>}

          <div className="text-center mt-6">
            <Link to="/articles">
              <Button variant="outline" className="border-brand-teal text-brand-teal hover:bg-brand-teal/10">
                {t('articles.viewAll')}
              </Button>
            </Link>
          </div>
        </div>}

      {/* Request Form Dialog */}
      <RequestFormDialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen} />
    </div>;
};
export default SearchLandingView;